import {
  MarktypUndergrund,
  Grundlaggning,
  Stommaterial,
  Bjalklagsmaterial,
  Fasadmaterial,
  Brunn,
  MuradeEldstader,
  KansligUtrustning,
  DimmensionerandeByggnadsmaterial,
  Ovrigt,
  Phase
} from "types";

/**
 * Converts enum value to its string representation
 * @param enumType The enum type
 * @param value The enum value or numeric value
 * @returns The string representation of the enum value
 */
export function getEnumName<T extends object>(enumType: T, value: number | T[keyof T]): string {
  return enumType[value as keyof T] as string;
}

/**
 * Get enum entries as options for select dropdown
 * @param enumType The enum type
 * @returns Array of {value, label} objects for select options
 */
export function getEnumOptions<T extends object>(enumType: T): { value: number, label: string }[] {
  const options: { value: number, label: string }[] = [];
  
  // Filter out reverse mappings (string -> number) that TypeScript adds to enums
  const keys = Object.keys(enumType).filter(key => isNaN(Number(key)));
  
  for (const key of keys) {
    const value = enumType[key as keyof T] as unknown as number;
    options.push({ value, label: key });
  }
  
  return options;
}

// Type guard for checking if a value is a valid enum value
export function isValidEnumValue<T extends object>(enumType: T, value: any): value is T[keyof T] {
  return Object.values(enumType).includes(value);
}

// Specialized helpers for document enums
export const documentEnums = {
  MarktypUndergrund: {
    getName: (value: number | MarktypUndergrund) => getEnumName(MarktypUndergrund, value),
    getOptions: () => getEnumOptions(MarktypUndergrund),
    isValid: (value: any) => isValidEnumValue(MarktypUndergrund, value),
  },
  Grundlaggning: {
    getName: (value: number | Grundlaggning) => getEnumName(Grundlaggning, value),
    getOptions: () => getEnumOptions(Grundlaggning),
    isValid: (value: any) => isValidEnumValue(Grundlaggning, value),
  },
  Stommaterial: {
    getName: (value: number | Stommaterial) => getEnumName(Stommaterial, value),
    getOptions: () => getEnumOptions(Stommaterial),
    isValid: (value: any) => isValidEnumValue(Stommaterial, value),
  },
  Bjalklagsmaterial: {
    getName: (value: number | Bjalklagsmaterial) => getEnumName(Bjalklagsmaterial, value),
    getOptions: () => getEnumOptions(Bjalklagsmaterial),
    isValid: (value: any) => isValidEnumValue(Bjalklagsmaterial, value),
  },
  Fasadmaterial: {
    getName: (value: number | Fasadmaterial) => getEnumName(Fasadmaterial, value),
    getOptions: () => getEnumOptions(Fasadmaterial),
    isValid: (value: any) => isValidEnumValue(Fasadmaterial, value),
  },
  Brunn: {
    getName: (value: number | Brunn) => getEnumName(Brunn, value),
    getOptions: () => getEnumOptions(Brunn),
    isValid: (value: any) => isValidEnumValue(Brunn, value),
  },
  MuradeEldstader: {
    getName: (value: number | MuradeEldstader) => getEnumName(MuradeEldstader, value),
    getOptions: () => getEnumOptions(MuradeEldstader),
    isValid: (value: any) => isValidEnumValue(MuradeEldstader, value),
  },
  KansligUtrustning: {
    getName: (value: number | KansligUtrustning) => getEnumName(KansligUtrustning, value),
    getOptions: () => getEnumOptions(KansligUtrustning),
    isValid: (value: any) => isValidEnumValue(KansligUtrustning, value),
  },
  DimmensionerandeByggnadsmaterial: {
    getName: (value: number | DimmensionerandeByggnadsmaterial) => getEnumName(DimmensionerandeByggnadsmaterial, value),
    getOptions: () => getEnumOptions(DimmensionerandeByggnadsmaterial),
    isValid: (value: any) => isValidEnumValue(DimmensionerandeByggnadsmaterial, value),
  },
  Ovrigt: {
    getName: (value: number | Ovrigt) => getEnumName(Ovrigt, value),
    getOptions: () => getEnumOptions(Ovrigt),
    isValid: (value: any) => isValidEnumValue(Ovrigt, value),
  },
  Phase: {
    getName: (value: number | Phase) => getEnumName(Phase, value),
    getOptions: () => getEnumOptions(Phase),
    isValid: (value: any) => isValidEnumValue(Phase, value),
  },
};